/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * Investor Notification Mapping Policy — PURE FUNCTIONS
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Converts internal system events into investor-safe notification payloads.
 * Every function is pure and deterministic — no side effects, no service calls.
 *
 * Safety rules:
 *   - NEVER expose: reconciliation, disbursement internals, enforcement verdicts
 *   - ALWAYS use clean language: "Payment completed", "Milestone verified"
 *   - NEVER include: raw error messages, internal IDs in message text
 *
 * @module services/policies/investorNotificationPolicy
 */

import type { InvestorNotificationPayload } from '@/types/investorNotifications';
import type { CanonicalInvestorEntityReference } from '@/types/investorEntityReference';

// ═══════════════════════════════════════════════════════════════════════════════
// INPUT TYPES (minimal shapes — no coupling to full service types)
// ═══════════════════════════════════════════════════════════════════════════════

/** Minimal milestone event input. */
export interface MilestoneEventInput {
    milestoneId: string;
    milestoneName: string;
    projectId: string;
    projectName: string;
    /** This policy accepts only the verified domain event, not delivery state. */
    verificationStatus: 'verified';
}

/** Minimal distribution event input. */
export interface DistributionEventInput {
    distributionId: string;
    projectId: string;
    projectName: string;
    grossAmount: number;
    netAmount: number;
    currency: string;
    status: 'pending' | 'approved' | 'paid' | 'on_hold' | 'failed';
}

/** Minimal evidence event input. */
export interface EvidenceEventInput {
    packId: string;
    milestoneId: string;
    projectId: string;
    projectName: string;
    itemCount: number;
}

/** Minimal release event input. */
export interface ReleaseEventInput {
    releaseId: string;
    milestoneId: string;
    milestoneName: string;
    projectId: string;
    projectName: string;
    amount: number;
    currency: string;
    status: 'completed' | 'failed' | 'simulated';
}

/** Minimal portfolio event input. */
export interface PortfolioEventInput {
    projectId?: string;
    projectName?: string;
    summary: string;
}

/** Minimal approval workflow event input. */
export interface ApprovalEventInput {
    workflowId: string;
    resourceName: string;
    projectId?: string;
    projectName?: string;
    decision?: 'approved' | 'rejected';
}

/** Minimal e-signature event input. */
export interface SignatureEventInput {
    requestId: string;
    documentId: string;
    projectId?: string;
    projectName?: string;
    status: 'sent' | 'completed' | 'declined';
}

/** Minimal finance approval event input. */
export interface FinanceApprovalEventInput {
    requestId: string;
    amount: number;
    currency: string;
    projectId?: string;
    projectName?: string;
    decision?: 'approved' | 'rejected';
    entityRef?: CanonicalInvestorEntityReference;
}

/** Minimal data room access event input. */
export interface DataRoomAccessEventInput {
    grantId: string;
    dataroomId: string;
    dataroomName?: string;
    projectId?: string;
    projectName?: string;
    scope: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAPPING FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Map a milestone verification event to an investor-safe notification.
 */
export function mapMilestoneEvent(input: MilestoneEventInput): InvestorNotificationPayload {
    return {
        type: 'milestone_verified',
        title: 'Milestone Verified',
        message: `"${input.milestoneName}" in ${input.projectName} has been verified and approved.`,
        severity: 'success',
        projectId: input.projectId,
        projectName: input.projectName,
        referenceId: input.milestoneId,
    };
}

/**
 * Map a distribution event to an investor-safe notification.
 * Hides internal disbursement details — only shows clean status.
 */
export function mapDistributionEvent(input: DistributionEventInput): InvestorNotificationPayload {
    const formatted = formatCurrency(input.netAmount, input.currency);

    switch (input.status) {
        case 'paid':
            return {
                type: 'distribution_paid',
                title: 'Distribution Paid',
                message: `A distribution of ${formatted} from ${input.projectName} has been completed.`,
                severity: 'success',
                projectId: input.projectId,
                projectName: input.projectName,
                referenceId: input.distributionId,
            };

        case 'pending':
        case 'approved':
            return {
                type: 'distribution_pending',
                title: 'Distribution Processing',
                message: `A distribution of ${formatted} from ${input.projectName} is being processed.`,
                severity: 'info',
                projectId: input.projectId,
                projectName: input.projectName,
                referenceId: input.distributionId,
            };

        case 'on_hold':
            return {
                type: 'distribution_pending',
                title: 'Distribution Blocked',
                message: `A distribution from ${input.projectName} is under review. No action is required from you.`,
                severity: 'critical',
                projectId: input.projectId,
                projectName: input.projectName,
                referenceId: input.distributionId,
            };

        case 'failed':
            // NEVER expose failure details — use generic language
            return {
                type: 'alert',
                title: 'Distribution Requires Attention',
                message: `A distribution from ${input.projectName} requires attention. Your account administrator has been notified.`,
                severity: 'critical',
                projectId: input.projectId,
                projectName: input.projectName,
                referenceId: input.distributionId,
            };
    }
}

/**
 * Map an evidence pack availability event to an investor-safe notification.
 */
export function mapEvidenceEvent(input: EvidenceEventInput): InvestorNotificationPayload {
    return {
        type: 'evidence_available',
        title: 'Documentation Available',
        message: `New verification documentation (${input.itemCount} item${input.itemCount !== 1 ? 's' : ''}) is available for review in ${input.projectName}.`,
        severity: 'info',
        projectId: input.projectId,
        projectName: input.projectName,
        referenceId: input.packId,
    };
}

/**
 * Map a capital release event to an investor-safe notification.
 * Hides enforcement verdicts and internal release mechanics.
 */
export function mapReleaseEvent(input: ReleaseEventInput): InvestorNotificationPayload {
    const formatted = formatCurrency(input.amount, input.currency);

    if (input.status === 'simulated') {
        return {
            type: 'release_completed',
            title: 'Capital Simulation Completed',
            message: `A simulated capital release of ${formatted} for "${input.milestoneName}" in ${input.projectName} has been recorded for review. No external funds were moved.`,
            severity: 'info',
            projectId: input.projectId,
            projectName: input.projectName,
            referenceId: input.releaseId,
        };
    }

    if (input.status === 'completed') {
        return {
            type: 'release_completed',
            title: 'Capital Release Completed',
            message: `A capital release of ${formatted} for "${input.milestoneName}" in ${input.projectName} has been completed.`,
            severity: 'success',
            projectId: input.projectId,
            projectName: input.projectName,
            referenceId: input.releaseId,
        };
    }

    // Failed releases → generic advisory (no internal details)
    return {
        type: 'alert',
        title: 'Release Update',
        message: `A capital release for "${input.milestoneName}" in ${input.projectName} is being reviewed. No action is required from you.`,
        severity: 'info',
        projectId: input.projectId,
        projectName: input.projectName,
        referenceId: input.releaseId,
    };
}

/**
 * Map a portfolio-level event to an investor-safe notification.
 */
export function mapPortfolioEvent(input: PortfolioEventInput): InvestorNotificationPayload {
    return {
        type: 'portfolio_update',
        title: 'Portfolio Update',
        message: input.summary,
        severity: 'info',
        projectId: input.projectId,
        projectName: input.projectName,
    };
}

/**
 * Map an approval workflow event to an investor-safe notification.
 */
export function mapApprovalEvent(input: ApprovalEventInput): InvestorNotificationPayload {
    if (input.decision === 'rejected') {
        return {
            type: 'approval_decided',
            title: 'Approval Requires Attention',
            message: `An approval for "${input.resourceName}"${input.projectName ? ` in ${input.projectName}` : ''} was not approved and may require further review.`,
            severity: 'warning',
            projectId: input.projectId,
            projectName: input.projectName,
            referenceId: input.workflowId,
        };
    }
    if (input.decision === 'approved') {
        return {
            type: 'approval_decided',
            title: 'Approval Completed',
            message: `"${input.resourceName}"${input.projectName ? ` in ${input.projectName}` : ''} has been fully approved.`,
            severity: 'success',
            projectId: input.projectId,
            projectName: input.projectName,
            referenceId: input.workflowId,
        };
    }
    return {
        type: 'approval_requested',
        title: 'Approval Requested',
        message: `"${input.resourceName}"${input.projectName ? ` in ${input.projectName}` : ''} has been submitted for approval.`,
        severity: 'info',
        projectId: input.projectId,
        projectName: input.projectName,
        referenceId: input.workflowId,
    };
}

/**
 * Map an e-signature event to an investor-safe notification.
 */
export function mapSignatureEvent(input: SignatureEventInput): InvestorNotificationPayload {
    if (input.status === 'completed') {
        return {
            type: 'signature_completed',
            title: 'Signature Completed',
            message: `All parties have signed the document${input.projectName ? ` for ${input.projectName}` : ''}.`,
            severity: 'success',
            projectId: input.projectId,
            projectName: input.projectName,
            referenceId: input.requestId,
        };
    }
    if (input.status === 'declined') {
        return {
            type: 'signature_completed',
            title: 'Signature Declined',
            message: `A signature request${input.projectName ? ` for ${input.projectName}` : ''} was declined. Your account administrator has been notified.`,
            severity: 'warning',
            projectId: input.projectId,
            projectName: input.projectName,
            referenceId: input.requestId,
        };
    }
    return {
        type: 'signature_requested',
        title: 'Signature Requested',
        message: `A document${input.projectName ? ` in ${input.projectName}` : ''} has been sent for signature.`,
        severity: 'info',
        projectId: input.projectId,
        projectName: input.projectName,
        referenceId: input.requestId,
    };
}

/**
 * Map a finance approval event to an investor-safe notification.
 * Hides internal risk levels and thresholds.
 */
export function mapFinanceApprovalEvent(input: FinanceApprovalEventInput): InvestorNotificationPayload {
    const formatted = formatCurrency(input.amount, input.currency);

    if (input.decision === 'approved') {
        return {
            type: 'finance_approval_decided',
            title: 'Finance Approval Granted',
            message: `A finance approval for ${formatted}${input.projectName ? ` in ${input.projectName}` : ''} has been granted.`,
            severity: 'success',
            projectId: input.projectId,
            projectName: input.projectName,
            referenceId: input.requestId,
            entityRef: input.entityRef,
        };
    }
    if (input.decision === 'rejected') {
        return {
            type: 'finance_approval_decided',
            title: 'Finance Approval Update',
            message: `A finance approval for ${formatted}${input.projectName ? ` in ${input.projectName}` : ''} requires further review.`,
            severity: 'warning',
            projectId: input.projectId,
            projectName: input.projectName,
            referenceId: input.requestId,
            entityRef: input.entityRef,
        };
    }
    return {
        type: 'finance_approval_requested',
        title: 'Finance Approval Requested',
        message: `A finance approval for ${formatted}${input.projectName ? ` in ${input.projectName}` : ''} has been submitted for review.`,
        severity: 'info',
        projectId: input.projectId,
        projectName: input.projectName,
        referenceId: input.requestId,
        entityRef: input.entityRef,
    };
}

/**
 * Map a data room access event to an investor-safe notification.
 */
export function mapDataRoomAccessEvent(input: DataRoomAccessEventInput): InvestorNotificationPayload {
    return {
        type: 'dataroom_access_granted',
        title: 'Data Room Access Granted',
        message: `You have been granted ${input.scope} access to ${input.dataroomName || 'a data room'}${input.projectName ? ` in ${input.projectName}` : ''}.`,
        severity: 'info',
        projectId: input.projectId,
        projectName: input.projectName,
        referenceId: input.grantId,
    };
}

// ═══════════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/** Format currency for investor-facing display. */
function formatCurrency(amount: number, currency: string): string {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(amount);
    } catch {
        return `${currency} ${amount.toLocaleString()}`;
    }
}
